import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBatchJobs } from '@/hooks/useBatchJobs';
import { format, startOfDay, endOfDay, subDays } from 'date-fns';
import type { EntityType, BatchStatus } from '@/types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Clock, Loader2, AlertCircle, CheckCircle2, XCircle, Zap, Timer, Calendar, ChevronRight } from 'lucide-react';

type DatePreset = 'today' | 'yesterday' | 'last7d' | 'last30d' | 'last90d' | 'all' | 'custom';

const PRESET_LABELS: Record<Exclude<DatePreset, 'custom'>, string> = {
  today: 'Today',
  yesterday: 'Yesterday',
  last7d: 'Last 7 days',
  last30d: 'Last 30 days',
  last90d: 'Last 90 days',
  all: 'All time',
};

// Returns an inclusive [start, end] millisecond range plus a human label,
// based on the active preset (or the custom from/to inputs when preset='custom').
function resolveRange(
  preset: DatePreset,
  customFrom: string,
  customTo: string,
): { start: number; end: number; label: string } {
  const now = new Date();
  switch (preset) {
    case 'today':
      return { start: startOfDay(now).getTime(), end: endOfDay(now).getTime(), label: 'Today' };
    case 'yesterday': {
      const y = subDays(now, 1);
      return { start: startOfDay(y).getTime(), end: endOfDay(y).getTime(), label: 'Yesterday' };
    }
    case 'last7d':
      return {
        start: startOfDay(subDays(now, 6)).getTime(),
        end: endOfDay(now).getTime(),
        label: 'Last 7 days',
      };
    case 'last30d':
      return {
        start: startOfDay(subDays(now, 29)).getTime(),
        end: endOfDay(now).getTime(),
        label: 'Last 30 days',
      };
    case 'last90d':
      return {
        start: startOfDay(subDays(now, 89)).getTime(),
        end: endOfDay(now).getTime(),
        label: 'Last 90 days',
      };
    case 'all':
      return { start: 0, end: endOfDay(now).getTime(), label: 'All time' };
    case 'custom': {
      // <input type="date"> emits YYYY-MM-DD; parse as local midnight.
      const s = customFrom ? startOfDay(new Date(`${customFrom}T00:00:00`)).getTime() : 0;
      const e = customTo
        ? endOfDay(new Date(`${customTo}T00:00:00`)).getTime()
        : endOfDay(now).getTime();
      return {
        start: s,
        end: e,
        label:
          customFrom && customTo
            ? `${customFrom} → ${customTo}`
            : customFrom
              ? `from ${customFrom}`
              : customTo
                ? `until ${customTo}`
                : 'All time',
      };
    }
  }
}

export default function BatchJobsTable() {
  const navigate = useNavigate();
  const [entityFilter, setEntityFilter] = useState<EntityType | undefined>();
  const [statusFilter, setStatusFilter] = useState<BatchStatus | undefined>();
  const [datePreset, setDatePreset] = useState<DatePreset>('last7d');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Get timezone info
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const dateRange = useMemo(
    () => resolveRange(datePreset, customFrom, customTo),
    [datePreset, customFrom, customTo],
  );

  const { data: batchJobs = [], isLoading, error } = useBatchJobs({
    entityType: entityFilter,
    status: statusFilter,
  });

  // Filter batch jobs by selected day
  const filteredBatchJobs = batchJobs.filter((job: any) => {
    return job.createdAt >= dateRange.start && job.createdAt <= dateRange.end;
  });

  // Pagination calculations
  const totalJobs = filteredBatchJobs.length;
  const totalPages = Math.ceil(totalJobs / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedJobs = filteredBatchJobs.slice(startIndex, endIndex);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [entityFilter, statusFilter, datePreset, customFrom, customTo]);

  const getStatusVariant = (status: BatchStatus): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'created':
        return 'secondary';
      case 'in_progress':
        return 'default';
      case 'ended':
        return 'outline';
      case 'failed':
        return 'destructive';
      case 'expired':
        return 'secondary';
      default:
        return 'secondary';
    }
  };

  const getEntityVariant = (entityType: EntityType): "default" | "secondary" | "outline" | "destructive" => {
    if (entityType === 'person') return 'default';
    if (entityType === 'company') return 'secondary';
    if (entityType === 'opportunity') return 'outline';
    if (entityType === 'note') return 'outline';
    if (entityType === 'list') return 'secondary';
    if (entityType === 'listEntry') return 'outline';
    return 'outline';
  };

  const getTriggerIcon = (triggerReason?: string) => {
    if (triggerReason === 'threshold') {
      return <Zap className="h-3 w-3" title="Triggered by threshold (50 docs)" />;
    }
    if (triggerReason === 'scheduled') {
      return <Timer className="h-3 w-3" title="Triggered by 10-min timer" />;
    }
    return null;
  };

  const formatDuration = (startMs: number, endMs?: number) => {
    const end = endMs || Date.now();
    const durationMs = end - startMs;
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);

    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            <span>Loading batch jobs...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="flex items-center justify-center py-12">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-4 w-4" />
            <span>Error loading batch jobs: {String(error)}</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Anthropic Batch Jobs</CardTitle>
            <CardDescription className="mt-1">
              Showing {filteredBatchJobs.length.toLocaleString()} of {batchJobs.length.toLocaleString()} batch{batchJobs.length !== 1 ? 'es' : ''} • {dateRange.label} ({userTimezone})
            </CardDescription>
          </div>
        </div>

        {/* Date Range Selector — presets + free-form custom from/to */}
        <div className="mt-4 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            {(Object.keys(PRESET_LABELS) as Array<keyof typeof PRESET_LABELS>).map((p) => (
              <Button
                key={p}
                variant={datePreset === p ? 'default' : 'outline'}
                size="sm"
                onClick={() => setDatePreset(p)}
              >
                {PRESET_LABELS[p]}
              </Button>
            ))}
            <Button
              variant={datePreset === 'custom' ? 'default' : 'outline'}
              size="sm"
              onClick={() => setDatePreset('custom')}
            >
              Custom
            </Button>
          </div>
          {datePreset === 'custom' && (
            <div className="flex flex-wrap items-end gap-3">
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">From</label>
                <Input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="w-[170px]"
                />
              </div>
              <div className="space-y-1">
                <label className="text-xs text-muted-foreground">To</label>
                <Input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="w-[170px]"
                />
              </div>
              {(customFrom || customTo) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setCustomFrom('');
                    setCustomTo('');
                  }}
                >
                  Clear
                </Button>
              )}
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-4 mt-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Entity Type</label>
            <Select
              value={entityFilter || 'all'}
              onValueChange={(value) =>
                setEntityFilter(value === 'all' ? undefined : (value as EntityType))
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="person">Person</SelectItem>
                <SelectItem value="company">Company</SelectItem>
                <SelectItem value="opportunity">Opportunity</SelectItem>
                <SelectItem value="note">Note</SelectItem>
                <SelectItem value="list">List</SelectItem>
                <SelectItem value="listEntry">List Entry</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">Status</label>
            <Select
              value={statusFilter || 'all'}
              onValueChange={(value) =>
                setStatusFilter(value === 'all' ? undefined : (value as BatchStatus))
              }
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="created">Created</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="ended">Ended</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
                <SelectItem value="expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        <div className="rounded-md border overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="min-w-[300px]">Batch ID</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">✓ Done</TableHead>
                <TableHead className="text-right">⚡ Processing</TableHead>
                <TableHead className="text-right">✗ Failed</TableHead>
                <TableHead>Trigger</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead aria-label="Open"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {paginatedJobs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-muted-foreground">
                    No batch jobs in {dateRange.label.toLowerCase()}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedJobs.map((job: any) => {
                  const requestCounts = job.requestCounts || {};
                  const succeeded = requestCounts.succeeded || 0;
                  const processing = requestCounts.processing || 0;
                  const failed = (requestCounts.errored || 0) + (requestCounts.expired || 0);

                  return (
                    <TableRow
                      key={job.batchId}
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => navigate(`/batches/${job.batchId}`)}
                    >
                      <TableCell className="font-mono text-xs min-w-[300px] max-w-[400px] break-all">
                        {job.batchId}
                      </TableCell>
                      <TableCell>
                        <Badge variant={getEntityVariant(job.entityType)}>
                          {job.entityType}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={getStatusVariant(job.status)}>
                          {job.status.replace('_', ' ')}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-semibold">
                        {job.documentCount}
                      </TableCell>
                      <TableCell className="text-right">
                        {succeeded > 0 && (
                          <span className="flex items-center justify-end gap-1 text-green-600">
                            <CheckCircle2 className="h-3 w-3" />
                            {succeeded}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {processing > 0 && (
                          <span className="flex items-center justify-end gap-1 text-blue-600">
                            <Loader2 className="h-3 w-3 animate-spin" />
                            {processing}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        {failed > 0 && (
                          <span className="flex items-center justify-end gap-1 text-red-600">
                            <XCircle className="h-3 w-3" />
                            {failed}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {getTriggerIcon(job.triggerReason)}
                          <span className="text-xs text-muted-foreground">
                            {job.triggerReason || '-'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {format(job.createdAt, 'MMM d, h:mm a')}
                        </div>
                      </TableCell>
                      <TableCell className="text-xs">
                        {formatDuration(job.createdAt, job.completedAt)}
                      </TableCell>
                      <TableCell>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination Controls */}
        {totalJobs > 0 && (
          <div className="flex items-center justify-between px-2 py-4">
            <div className="text-sm text-muted-foreground">
              Showing {startIndex + 1}-{Math.min(endIndex, totalJobs)} of {totalJobs.toLocaleString()} batch jobs
            </div>

            <div className="flex items-center gap-6">
              {/* Page Size Selector */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">Rows per page:</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(Number(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[80px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="25">25</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                    <SelectItem value="200">200</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Page Navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(1)}
                  disabled={currentPage === 1}
                >
                  First
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <div className="text-sm">
                  Page {currentPage} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(totalPages)}
                  disabled={currentPage === totalPages}
                >
                  Last
                </Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
